import { useAidaSessionStore } from '../stores/aidaSessionStore'
import { ref } from 'vue'
import { useAppStore } from '../stores/appStore'
import { t } from './Translation'
import { useToast } from './useToast'
import type { MessageAttachment } from './AttachmentsValidation'

export function loadAidaLibraryFromExternalUrl(): void {
  let aidaLibraryScript = document.createElement('script')
  aidaLibraryScript.setAttribute('src', import.meta.env.VITE_AIDA_LIBRARY_URL)
  document.head.appendChild(aidaLibraryScript)
}

export async function initializeAidaLibraryAsync(): Promise<any | null> {
  try {
    const aida = await waitForGlobal<typeof window.aida>('aida')
    return aida?.AIDA_Library?.initialize(
      import.meta.env.VITE_AIDA_LIBRARY_CLIENT_NAME,
      import.meta.env.VITE_AIDA_LIBRARY_CLIENT_KEY,
      import.meta.env.VITE_AIDA_LIBRARY_USER_ID
    )
  } catch (err) {
    const aidaErrorMessage =
      t('chat.loadAidaError') === 'chat.loadAidaError'
        ? 'Error - Failed to load'
        : t('chat.loadAidaError')
    console.error(aidaErrorMessage, err)
    useToast().error(`${aidaErrorMessage}`)
    return null
  }
}

export function waitForGlobal<T = any>(
  key: string,
  checkInterval = 100,
  timeout = 10000
): Promise<T> {
  return new Promise((resolve, reject) => {
    const start = Date.now()

    const check = () => {
      const globalValue = (window as any)[key]
      if (globalValue !== undefined) {
        resolve(globalValue as T)
      } else if (Date.now() - start >= timeout) {
        reject(new Error(`Global object "${key}" not found within ${timeout}ms`))
      } else {
        setTimeout(check, checkInterval)
      }
    }

    check()
  })
}

export interface AskQuestionResult {
  answer: string | null
  error?: string
}

export async function askQuestionAsync(
  aidaLibrary: any,
  question: string
): Promise<AskQuestionResult> {
  const appStore = useAppStore()

  let productPrefixText = ''
  const product = ref<string>('')
  let questionEnhanced = question

  if (appStore.configuration?.components.productSelect.visible && appStore.selectedProduct) {
    productPrefixText = t('productSelect.productPrefix')
    product.value = appStore.selectedProduct || ''
    questionEnhanced = `${productPrefixText} ${product.value}:\n${question}`
  }
  if (appStore.configuration?.components.productFromUrl.enabled && appStore.productFromUrl) {
    productPrefixText = t('productFromUrl.productPrefix')
    product.value = appStore.productFromUrl || ''
    questionEnhanced = `${productPrefixText} ${product.value}:\n${question}`
  }

  try {
    //Triggers when we cannot load the AIDA library
    if (!aidaLibrary || typeof aidaLibrary.askQuestion !== 'function') {
      const errorMessage = t('chat.loadAidaError')
      console.error(errorMessage)
      useToast().error(errorMessage)
      return { answer: null, error: errorMessage }
    }

    const answer = await aidaLibrary.askQuestion(questionEnhanced)
    useAidaSessionStore().setAidaSessionIdIfNew(answer?.users_session_id)
    return { answer: answer?.aida_answer ?? null }
  } catch (error: any) {
    let errorMessage: string

    const errorId = String(error?.aida_error_id ?? '')
    const description = String(error?.aida_error_description ?? '').toLowerCase()

    const is400Error = errorId.startsWith('400')
    const isContextExceeded =
      description.includes('context_length_exceeded') ||
      description.includes("this model's maximum context length is")

    const isUploadNotAllowed =
      description.includes('detected mime type') && description.includes('is not allowed')

    const isAttachmentContextTooLarge =
      errorId.startsWith('422') &&
      description.includes('aida client attached files context too long')

    if ((is400Error && isContextExceeded) || isAttachmentContextTooLarge) {
      errorMessage = t('chat.contextLengthExceededError')
    } else if (isUploadNotAllowed) {
      errorMessage = t('chat.uploadError')
    } else {
      errorMessage = t('chat.questionError')
    }

    useToast().error(errorMessage)
    console.error(errorMessage, `- Error code: ${errorId} | Description: ${description}`)
    return { answer: null, error: errorMessage }
  } finally {
    if (aidaLibrary != null) {
      removeAllFilesFromUploadList(aidaLibrary)
    }
  }
}

export function addFilesForUpload(aidaLibrary: any, files: MessageAttachment[]) {
  const filesOnly = files.map(f => f.file).filter((f): f is File => !!f)

  try {
    aidaLibrary.addFilesForUpload(filesOnly)
  } catch (error: any) {
    useToast().error(t('chat.uploadError'))
    console.error(t('chat.uploadError'), error?.message)
  }
}

export function removeFileFromUploadList(aidaLibrary: any, fileName: string) {
  try {
    aidaLibrary.removeFileFromUploadList(fileName)
  } catch (error: any) {
    useToast().error(t('chat.uploadRemoveError'))
    console.error(t('chat.uploadRemoveError'), error?.message)
  }
}

export function removeAllFilesFromUploadList(aidaLibrary: any) {
  try {
    aidaLibrary.clearFiles()
  } catch (error: any) {
    useToast().error(t('chat.uploadRemoveError'))
    console.error(t('chat.uploadRemoveError'), error?.message)
  }
}
